import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getAuthToken } from '../services/authUtils';
import { API_URL, ALPACA_CLIENT_ID, ALPACA_REDIRECT_URI } from '../config';

// ── PKCE helpers (RFC 7636) ─────────────────────────────────

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(digest);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface AlpacaAccount {
  equity: string;
  buying_power: string;
  account_number: string;
}

interface AlpacaContextType {
  isConnected: boolean;
  isLoading: boolean;
  paperConnected: boolean;
  liveConnected: boolean;
  alpacaAccount: AlpacaAccount | null;
  connectPaper: () => void;
  connectLive: () => void;
  disconnect: (env?: 'paper' | 'live') => Promise<void>;
  refreshStatus: () => Promise<void>;
}

const AlpacaContext = createContext<AlpacaContextType | undefined>(undefined);

export const AlpacaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [paperConnected, setPaperConnected] = useState(false);
  const [liveConnected, setLiveConnected] = useState(false);
  const [alpacaAccount, setAlpacaAccount] = useState<AlpacaAccount | null>(null);

  const refreshStatus = useCallback(async () => {
    if (!user) {
      setIsConnected(false);
      setPaperConnected(false);
      setLiveConnected(false);
      setAlpacaAccount(null);
      setIsLoading(false);
      return;
    }

    try {
      const token = await getAuthToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      const resp = await fetch(`${API_URL}/api/alpaca/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!resp.ok) {
        throw new Error(`Status check failed: ${resp.status}`);
      }

      const data = await resp.json();
      setIsConnected(data.connected || false);
      setPaperConnected(data.paper_connected || false);
      setLiveConnected(data.live_connected || false);
      setAlpacaAccount(data.account || null);
    } catch (err) {
      console.error('Failed to check Alpaca status:', err);
      setIsConnected(false);
      setPaperConnected(false);
      setLiveConnected(false);
      setAlpacaAccount(null);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const redirectToOAuth = async (env: 'paper' | 'live') => {
    const csrfState = crypto.randomUUID();
    sessionStorage.setItem('alpaca_oauth_state', csrfState);
    sessionStorage.setItem('alpaca_oauth_env', env);

    // PKCE: generate code_verifier (43-128 chars) and code_challenge (S256)
    const codeVerifier = generateCodeVerifier();
    sessionStorage.setItem('alpaca_pkce_verifier', codeVerifier);
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: ALPACA_CLIENT_ID,
      redirect_uri: ALPACA_REDIRECT_URI,
      state: csrfState,
      scope: 'account:write trading data',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    const url = `https://app.alpaca.markets/oauth/authorize?${params.toString()}&env=${env}`;
    window.location.href = url;
  };

  const connectPaper = () => redirectToOAuth('paper');
  const connectLive = () => redirectToOAuth('live');

  const disconnect = async (env?: 'paper' | 'live') => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const url = env ? `${API_URL}/api/alpaca/disconnect?env=${env}` : `${API_URL}/api/alpaca/disconnect`;

      await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      await refreshStatus();
    } catch (err) {
      console.error('Failed to disconnect Alpaca:', err);
    }
  };

  return (
    <AlpacaContext.Provider
      value={{
        isConnected,
        isLoading,
        paperConnected,
        liveConnected,
        alpacaAccount,
        connectPaper,
        connectLive,
        disconnect,
        refreshStatus,
      }}
    >
      {children}
    </AlpacaContext.Provider>
  );
};

export const useAlpaca = (): AlpacaContextType => {
  const context = useContext(AlpacaContext);
  if (!context) {
    throw new Error('useAlpaca must be used within an AlpacaProvider');
  }
  return context;
};
